import { oom } from "@mixitone/oom";
import SupabaseDatabaseAdapter from "@mixitone/oom/adapters/SupabaseDatabaseAdapter";
import TauriDatabaseAdapter from "@mixitone/oom/adapters/TauriDatabaseAdapter";
import { AgentChat, AgentMessage } from "@mixitone/models";
import {
  captureException,
  initTelemetry,
  registerTelemetryContext,
  TelemetryProvider,
} from "@mixitone/telemetry";
import { app } from "@tauri-apps/api";
import Session from "components/Pages/Session";
import { TauriSupabaseDatabaseAdapter } from "lib/adapters/TauriSupabaseDatabaseAdapter";
import { isTestMode } from "lib/environment";
import { syncAccountBroadcastTestHook } from "lib/accountBroadcastTestHook";
import { SupabaseSessionAdapter } from "lib/session/SupabaseSessionAdapter";
import { TauriSessionAdapter } from "lib/session/TauriSessionAdapter";
import { createSupabase } from "lib/supabaseClient";
import { startAppViewportHeightSync } from "lib/appViewport";
import { configureManualScrollRestoration } from "lib/scroll";
import { setupStaleAssetRecovery } from "@mixitone/util";
import tauri from "lib/tauri";
import { wireTauriBroadcastSyncWake } from "lib/tauri/broadcastSyncWake";
import { loadFrontendBuildInfo } from "updater/frontendBuildInfo";
import { MockTauri } from "lib/tauri/MockTauri";
import ReactDOM from "react-dom/client";
import "./index.css";

configureManualScrollRestoration();
startAppViewportHeightSync();
setupStaleAssetRecovery();

// @ts-expect-error
window.__TEST_MODE = isTestMode();
if (isTestMode()) {
  document.documentElement.style.setProperty("--view-transition-duration", "120ms");
  oom.configure({
    accountBroadcastSubscribedListener: syncAccountBroadcastTestHook,
  });
  syncAccountBroadcastTestHook(false);
}

// @ts-expect-error
if (window.__TAURI_TEST_MODE) {
  MockTauri.setup();
} else {
  if (!isTestMode()) {
    initTelemetry();
    oom.configure({ exceptionHandler: (error, extra) => captureException(error, { extra }) });

    // Set Tauri native + frontend build identity in GlitchTip/Sentry
    if (tauri.enabled) {
      Promise.all([app.getVersion(), loadFrontendBuildInfo()])
        .then(async ([version, buildInfo]) => {
          registerTelemetryContext({
            app_version: version,
            frontend_version: buildInfo.frontendVersion,
            frontend_sha: buildInfo.frontendSha,
            frontend_sequence: String(buildInfo.frontendSequence),
            platform: "tauri",
            frontend_channel: buildInfo.channel,
          });
          await tauri.invoke("set_frontend_build_identity", {
            frontendVersion: buildInfo.frontendVersion,
            frontendSha: buildInfo.frontendSha,
            frontendSequence: buildInfo.frontendSequence,
          });
        })
        .catch((err) => {
          console.error("Failed to set Tauri version tags for telemetry:", err);
        });
    }
  }

  const supabase = createSupabase();
  if (tauri.enabled) {
    const tauriAdapter = new TauriDatabaseAdapter(tauri);
    oom.configure({
      adapter: tauriAdapter,
      supabaseClient: supabase,
      realtimeMode: "hybrid",
    });
    wireTauriBroadcastSyncWake(supabase);
    window.sessionAdapter = new TauriSessionAdapter();
    // Configure AgentChat to use Supabase adapter with fresh tokens from Tauri
    AgentChat.adapter = new TauriSupabaseDatabaseAdapter(supabase);
    AgentMessage.adapter = new TauriSupabaseDatabaseAdapter(supabase);
  } else {
    oom.configure({
      adapter: new SupabaseDatabaseAdapter(supabase),
      supabaseClient: supabase,
      realtimeMode: "hybrid",
    });
    window.sessionAdapter = new SupabaseSessionAdapter(supabase);
  }

  ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
    <TelemetryProvider>
      <Session />
    </TelemetryProvider>,
  );
}
